import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { AnimatePresence, animate, motion } from 'motion/react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/shadcn/avatar'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent } from '@/components/shadcn/card'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Skeleton } from '@/components/shadcn/skeleton'
import { cn } from '@/lib/utils'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { SearchInput } from '../shared/SearchInput'
import { getComponentHeights } from '../utils'
import type { DataTableV2Action } from '../DataTableV2/types'
import {
  buildOrgChartHierarchy,
  deriveOrgChartLegend,
  getOrgChartCanvasEdges,
  getOrgChartCanvasRows,
  getOrgChartColor,
  getVisibleOrgChartNodeIds,
  resolveOrgChartDisplay,
  type OrgChartHierarchy,
  type OrgChartHierarchyNode,
  type OrgChartLegendEntry,
} from './orgChartModel'
import { OrgChartCardActions } from './OrgChartCardActions'
import { OrgChartDataSchema, OrgChartRecord, OrgChartUISchema } from './types'

const EMPTY_ORG_CHART_RECORDS: OrgChartRecord[] = []
const CANVAS_NODE_WIDTH = 224
const CANVAS_CARD_HEIGHT = 132
const CANVAS_HORIZONTAL_GAP = 32
const CANVAS_ROW_GAP = 64
const CANVAS_PADDING = 48
const CANVAS_BOUNDARY_LINE_LENGTH = 32
const CANVAS_BOUNDARY_BUTTON_SIZE = 28
const CANVAS_TOP_BOUNDARY_SPACE = CANVAS_BOUNDARY_LINE_LENGTH + CANVAS_BOUNDARY_BUTTON_SIZE
const CANVAS_CONNECTOR_STROKE = 'hsl(var(--border))'
const MIN_CANVAS_SCALE = 0.5
const MAX_CANVAS_SCALE = 3
const CANVAS_CAMERA_SPRING = { type: 'spring' as const, stiffness: 260, damping: 28 }

interface CanvasTransform {
  x: number
  y: number
  scale: number
}

interface CanvasPosition {
  x: number
  y: number
}

function getCenteredCanvasTransform(
  focusX: number,
  focusY: number,
  scale: number,
  viewport: { clientWidth: number; clientHeight: number }
): CanvasTransform {
  return {
    scale,
    x: viewport.clientWidth / 2 - focusX * scale,
    y: viewport.clientHeight / 2 - focusY * scale,
  }
}

function OrgChartAvatar({
  display,
  size = 'sm',
}: {
  display: ReturnType<typeof resolveOrgChartDisplay>
  size?: 'default' | 'sm' | 'lg'
}) {
  return (
    <Avatar size={size}>
      {display.avatarUrl && <AvatarImage src={display.avatarUrl} alt="" />}
      <AvatarFallback>{display.initials}</AvatarFallback>
    </Avatar>
  )
}

interface OrgChartSidebarProps {
  hierarchy: OrgChartHierarchy
  sidebarMapping: OrgChartUISchema['sidebar']
  visibleNodeIds: Set<string>
  search: string
  selectedId: string | null
  expandedIds: Set<string>
  isCollapsed: boolean
  onSearchChange: (value: string) => void
  onSelect: (id: string) => void
  onToggleExpanded: (id: string) => void
  onToggleCollapsed: () => void
}

function OrgChartSidebar({
  hierarchy,
  sidebarMapping,
  visibleNodeIds,
  search,
  selectedId,
  expandedIds,
  isCollapsed,
  onSearchChange,
  onSelect,
  onToggleExpanded,
  onToggleCollapsed,
}: OrgChartSidebarProps) {
  const forceExpandMatches = search.trim().length > 0
  const sidebarItemElementsRef = useRef(new Map<string, HTMLButtonElement>())

  const registerSidebarItem = useCallback((id: string, element: HTMLButtonElement | null) => {
    if (element) {
      sidebarItemElementsRef.current.set(id, element)
    } else {
      sidebarItemElementsRef.current.delete(id)
    }
  }, [])

  useLayoutEffect(() => {
    if (isCollapsed || !selectedId) {
      return
    }

    sidebarItemElementsRef.current.get(selectedId)?.scrollIntoView({ block: 'nearest' })
  }, [expandedIds, isCollapsed, selectedId])

  const renderNode = (node: OrgChartHierarchyNode, depth: number): ReactNode => {
    if (!visibleNodeIds.has(node.record.id)) {
      return null
    }

    const display = resolveOrgChartDisplay(node.record, sidebarMapping)
    const visibleChildren = node.children.filter((child) => visibleNodeIds.has(child.record.id))
    const hasChildren = visibleChildren.length > 0
    const isExpanded = hasChildren && (forceExpandMatches || expandedIds.has(node.record.id))
    const isSelected = node.record.id === selectedId

    return (
      <div key={node.record.id}>
        <div className="flex min-w-0 items-center gap-1 py-0.5" style={{ paddingLeft: depth * 16 }}>
          {hasChildren ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${display.title}`}
              aria-expanded={isExpanded}
              onClick={() => onToggleExpanded(node.record.id)}
            >
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </Button>
          ) : (
            <span className="size-6 shrink-0" aria-hidden="true" />
          )}
          <button
            ref={(element) => registerSidebarItem(node.record.id, element)}
            type="button"
            aria-current={isSelected ? 'true' : undefined}
            onClick={() => onSelect(node.record.id)}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-accent/60',
              isSelected && 'bg-accent text-accent-foreground'
            )}
          >
            <OrgChartAvatar display={display} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{display.title}</span>
              {display.subtitle && display.subtitle !== display.title && (
                <span className="block truncate text-xs text-muted-foreground">{display.subtitle}</span>
              )}
            </span>
          </button>
        </div>
        {isExpanded && <div>{visibleChildren.map((child) => renderNode(child, depth + 1))}</div>}
      </div>
    )
  }

  if (isCollapsed) {
    return (
      <aside className="flex w-12 shrink-0 flex-col items-center border-r border-border">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="mt-2"
          aria-label="Expand organisation chart sidebar"
          onClick={onToggleCollapsed}
        >
          <PanelLeftOpen />
        </Button>
      </aside>
    )
  }

  return (
    <aside className="flex w-80 min-w-0 shrink-0 flex-col border-r border-border">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder="Search people..."
          className="min-w-0 flex-1"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Collapse organisation chart sidebar"
          onClick={onToggleCollapsed}
        >
          <PanelLeftClose />
        </Button>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="p-2">
          {visibleNodeIds.size > 0 ? (
            hierarchy.roots.map((root) => renderNode(root, 0))
          ) : (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No people match your search.</p>
          )}
        </div>
      </ScrollArea>
    </aside>
  )
}

interface OrgChartCanvasProps {
  hierarchy: OrgChartHierarchy
  rows: OrgChartHierarchyNode[][]
  selectedId: string | null
  mapping: OrgChartUISchema['canvas']
  colorByKey: string | null
  legend: OrgChartLegendEntry[]
  cardActions?: DataTableV2Action[]
  onSelect: (id: string) => void
}

function OrgChartCanvas({
  hierarchy,
  rows,
  selectedId,
  mapping,
  colorByKey,
  legend,
  cardActions = [],
  onSelect,
}: OrgChartCanvasProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const cardElementsRef = useRef(new Map<string, HTMLDivElement>())
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null)
  const transformRef = useRef<CanvasTransform>({ x: 0, y: 0, scale: 1 })
  const [transform, setTransform] = useState<CanvasTransform>(transformRef.current)
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({})
  const centeredRootIdRef = useRef<string | undefined>(undefined)
  const cameraAnimationRef = useRef<{ stop: () => void } | null>(null)
  transformRef.current = transform

  const stopCameraAnimation = useCallback(() => {
    cameraAnimationRef.current?.stop()
    cameraAnimationRef.current = null
  }, [])

  const registerCardElement = useCallback((id: string, element: HTMLDivElement | null) => {
    if (element) {
      cardElementsRef.current.set(id, element)
    } else {
      cardElementsRef.current.delete(id)
    }
  }, [])

  const measureCards = useCallback(() => {
    setCardHeights((current) => {
      const next: Record<string, number> = {}
      cardElementsRef.current.forEach((element, id) => {
        // offsetHeight is in canvas coordinates; getBoundingClientRect() includes the zoom transform.
        next[id] = element.offsetHeight
      })

      const currentIds = Object.keys(current)
      const nextIds = Object.keys(next)
      const hasChanged = currentIds.length !== nextIds.length || nextIds.some((id) => current[id] !== next[id])
      return hasChanged ? next : current
    })
  }, [])

  useLayoutEffect(() => {
    measureCards()
    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(measureCards)
    cardElementsRef.current.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [measureCards, rows, mapping, colorByKey, legend])

  const parentId = selectedId ? (hierarchy.parentById.get(selectedId) ?? null) : null
  const parentNode = parentId === null ? undefined : hierarchy.nodesById.get(parentId)

  const layout = useMemo(() => {
    const positions = new Map<string, CanvasPosition>()
    const rowNodeIds = rows.map((row) => new Set(row.map((node) => node.record.id)))
    const subtreeWidths = new Map<string, number>()

    const getVisibleChildren = (node: OrgChartHierarchyNode, rowIndex: number) => {
      const nextRowIds = rowNodeIds[rowIndex + 1]
      return nextRowIds ? node.children.filter((child) => nextRowIds.has(child.record.id)) : []
    }

    const getSubtreeWidth = (node: OrgChartHierarchyNode, rowIndex: number): number => {
      const cachedWidth = subtreeWidths.get(node.record.id)
      if (cachedWidth !== undefined) {
        return cachedWidth
      }

      const children = getVisibleChildren(node, rowIndex)
      const width =
        children.length === 0
          ? CANVAS_NODE_WIDTH
          : children.reduce((total, child) => total + getSubtreeWidth(child, rowIndex + 1), 0) +
            (children.length - 1) * CANVAS_HORIZONTAL_GAP
      subtreeWidths.set(node.record.id, width)
      return width
    }

    const rowYPositions: number[] = []
    const rowCenterYs: number[] = []
    let nextRowY = CANVAS_PADDING + (parentId !== null ? CANVAS_TOP_BOUNDARY_SPACE : 0)

    rows.forEach((row) => {
      rowYPositions.push(nextRowY)
      const rowHeight = Math.max(
        CANVAS_CARD_HEIGHT,
        ...row.map((node) => cardHeights[node.record.id] ?? CANVAS_CARD_HEIGHT)
      )
      rowCenterYs.push(nextRowY + rowHeight / 2)
      nextRowY += rowHeight + CANVAS_ROW_GAP
    })

    const root = rows[0]?.[0]
    const treeWidth = root ? getSubtreeWidth(root, 0) : CANVAS_NODE_WIDTH
    const graphWidth = Math.max(760, treeWidth + CANVAS_PADDING * 2)
    const treeStartX = (graphWidth - treeWidth) / 2

    const placeNode = (node: OrgChartHierarchyNode, rowIndex: number, startX: number) => {
      const subtreeWidth = getSubtreeWidth(node, rowIndex)
      positions.set(node.record.id, {
        x: startX + (subtreeWidth - CANVAS_NODE_WIDTH) / 2,
        y: rowYPositions[rowIndex],
      })

      let nextChildX = startX
      getVisibleChildren(node, rowIndex).forEach((child) => {
        placeNode(child, rowIndex + 1, nextChildX)
        nextChildX += getSubtreeWidth(child, rowIndex + 1) + CANVAS_HORIZONTAL_GAP
      })
    }

    if (root) {
      placeNode(root, 0, treeStartX)
    }

    return { graphWidth, graphHeight: nextRowY + CANVAS_PADDING, positions, rowCenterYs }
  }, [cardHeights, parentId, rows])
  const layoutRef = useRef(layout)
  layoutRef.current = layout

  const edges = useMemo(() => getOrgChartCanvasEdges(hierarchy, selectedId), [hierarchy, selectedId])
  const visibleCardActions = useMemo(() => cardActions.filter((action) => action.isVisible), [cardActions])
  const connectorLaneYs = useMemo(() => {
    const laneYs = new Map<string, number>()

    rows.forEach((row, rowIndex) => {
      const nextRow = rows[rowIndex + 1]
      if (!nextRow || nextRow.length === 0) {
        return
      }

      const sourceRowBottom = Math.max(
        ...row.map((node) => {
          const position = layout.positions.get(node.record.id)
          return position ? position.y + (cardHeights[node.record.id] ?? CANVAS_CARD_HEIGHT) : 0
        })
      )
      const targetY = layout.positions.get(nextRow[0].record.id)?.y ?? sourceRowBottom + CANVAS_ROW_GAP
      const laneY = sourceRowBottom + (targetY - sourceRowBottom) / 2

      row.forEach((node) => {
        laneYs.set(node.record.id, laneY)
      })
    })

    return laneYs
  }, [cardHeights, layout, rows])

  const selectedPosition = selectedId ? layout.positions.get(selectedId) : undefined
  const parentDisplay = parentNode ? resolveOrgChartDisplay(parentNode.record, mapping) : undefined
  const levelThreeNodesWithChildren = (rows[2] ?? []).filter((node) => node.children.length > 0)

  const centerSelectedRoot = useCallback(
    (smooth: boolean) => {
      if (!selectedId || centeredRootIdRef.current === selectedId) {
        return false
      }

      const currentLayout = layoutRef.current
      const position = currentLayout.positions.get(selectedId)
      const viewport = viewportRef.current
      const verticalFocusY = currentLayout.rowCenterYs[1] ?? currentLayout.rowCenterYs[0]
      if (
        !position ||
        verticalFocusY === undefined ||
        !viewport ||
        viewport.clientWidth === 0 ||
        viewport.clientHeight === 0
      ) {
        return false
      }

      const scale = transformRef.current.scale
      const target = getCenteredCanvasTransform(
        position.x + CANVAS_NODE_WIDTH / 2,
        verticalFocusY,
        scale,
        viewport
      )
      centeredRootIdRef.current = selectedId
      stopCameraAnimation()

      if (!smooth) {
        transformRef.current = target
        setTransform(target)
        return true
      }

      const from = transformRef.current
      let latestX = from.x
      let latestY = from.y
      const apply = () => {
        const next = { x: latestX, y: latestY, scale: from.scale }
        transformRef.current = next
        setTransform(next)
      }

      const animationX = animate(from.x, target.x, {
        ...CANVAS_CAMERA_SPRING,
        onUpdate: (value) => {
          latestX = value
          apply()
        },
      })
      const animationY = animate(from.y, target.y, {
        ...CANVAS_CAMERA_SPRING,
        onUpdate: (value) => {
          latestY = value
          apply()
        },
      })
      cameraAnimationRef.current = {
        stop: () => {
          animationX.stop()
          animationY.stop()
        },
      }
      return true
    },
    [selectedId, stopCameraAnimation]
  )

  useLayoutEffect(() => {
    centerSelectedRoot(centeredRootIdRef.current !== undefined)
  }, [centerSelectedRoot, selectedId])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(() => {
      centerSelectedRoot(centeredRootIdRef.current !== undefined)
    })
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [centerSelectedRoot])

  useEffect(() => () => stopCameraAnimation(), [stopCameraAnimation])

  const zoomBy = useCallback(
    (factor: number, centerX?: number, centerY?: number) => {
      stopCameraAnimation()
      setTransform((current) => {
        const scale = Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, current.scale * factor))
        const ratio = scale / current.scale
        const viewport = viewportRef.current
        const x = centerX ?? (viewport?.clientWidth ?? 0) / 2
        const y = centerY ?? (viewport?.clientHeight ?? 0) / 2
        return {
          scale,
          x: x - (x - current.x) * ratio,
          y: y - (y - current.y) * ratio,
        }
      })
    },
    [stopCameraAnimation]
  )

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) {
      return
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      const bounds = viewport.getBoundingClientRect()
      zoomBy(Math.exp(-event.deltaY * 0.0015), event.clientX - bounds.left, event.clientY - bounds.top)
    }

    viewport.addEventListener('wheel', handleWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', handleWheel)
  }, [zoomBy])

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return
      }

      stopCameraAnimation()
      event.currentTarget.setPointerCapture(event.pointerId)
      const current = transformRef.current
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
      }
    },
    [stopCameraAnimation]
  )

  const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) {
      return
    }

    setTransform((current) => ({
      ...current,
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    }))
  }, [])

  const onPointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  return (
    <div
      ref={viewportRef}
      className="relative min-h-0 min-w-0 flex-1 cursor-grab touch-none overflow-hidden bg-muted/10 active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-label="Organisation chart canvas. Drag to pan and scroll to zoom."
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          width: layout.graphWidth,
          height: layout.graphHeight,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
        }}
      >
        <svg
          className="pointer-events-none absolute inset-0"
          width={layout.graphWidth}
          height={layout.graphHeight}
          viewBox={`0 0 ${layout.graphWidth} ${layout.graphHeight}`}
          aria-hidden="true"
        >
          {edges.map((edge) => {
            const source = layout.positions.get(edge.sourceId)
            const target = layout.positions.get(edge.targetId)
            if (!source || !target) {
              return null
            }

            const sourceX = source.x + CANVAS_NODE_WIDTH / 2
            const sourceY = source.y + (cardHeights[edge.sourceId] ?? CANVAS_CARD_HEIGHT)
            const targetX = target.x + CANVAS_NODE_WIDTH / 2
            const targetY = target.y
            const laneY = connectorLaneYs.get(edge.sourceId) ?? sourceY + (targetY - sourceY) / 2
            return (
              <path
                key={`${edge.sourceId}-${edge.targetId}`}
                d={`M ${sourceX} ${sourceY} V ${laneY} H ${targetX} V ${targetY}`}
                fill="none"
                stroke={CANVAS_CONNECTOR_STROKE}
                strokeWidth="2"
              />
            )
          })}
          {parentNode && selectedPosition && (
            <path
              d={`M ${selectedPosition.x + CANVAS_NODE_WIDTH / 2} ${selectedPosition.y} V ${
                selectedPosition.y - CANVAS_BOUNDARY_LINE_LENGTH
              }`}
              fill="none"
              stroke={CANVAS_CONNECTOR_STROKE}
              strokeWidth="2"
              strokeDasharray="4 4"
              strokeLinecap="round"
            />
          )}
          {levelThreeNodesWithChildren.map((node) => {
            const position = layout.positions.get(node.record.id)
            if (!position) {
              return null
            }

            const cardHeight = cardHeights[node.record.id] ?? CANVAS_CARD_HEIGHT
            const cardBottom = position.y + cardHeight
            return (
              <path
                key={`descendant-boundary-${node.record.id}`}
                d={`M ${position.x + CANVAS_NODE_WIDTH / 2} ${cardBottom} V ${
                  cardBottom + CANVAS_BOUNDARY_LINE_LENGTH
                }`}
                fill="none"
                stroke={CANVAS_CONNECTOR_STROKE}
                strokeWidth="2"
                strokeDasharray="4 4"
                strokeLinecap="round"
              />
            )
          })}
        </svg>
        {parentNode && parentDisplay && selectedPosition && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute z-10 rounded-full border border-dashed border-border"
            style={{
              left: selectedPosition.x + CANVAS_NODE_WIDTH / 2 - CANVAS_BOUNDARY_BUTTON_SIZE / 2,
              top: selectedPosition.y - CANVAS_BOUNDARY_LINE_LENGTH - CANVAS_BOUNDARY_BUTTON_SIZE,
            }}
            aria-label={`View parent ${parentDisplay.title}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onSelect(parentNode.record.id)}
          >
            <ArrowUp />
          </Button>
        )}
        {levelThreeNodesWithChildren.map((node) => {
          const position = layout.positions.get(node.record.id)
          if (!position) {
            return null
          }

          const display = resolveOrgChartDisplay(node.record, mapping)
          const parentId = hierarchy.parentById.get(node.record.id) ?? null
          if (parentId === null) {
            return null
          }

          const cardHeight = cardHeights[node.record.id] ?? CANVAS_CARD_HEIGHT
          const cardBottom = position.y + cardHeight
          return (
            <Button
              key={`descendant-boundary-button-${node.record.id}`}
              type="button"
              variant="ghost"
              size="icon-sm"
              className="absolute z-10 rounded-full border border-dashed border-border"
              style={{
                left: position.x + CANVAS_NODE_WIDTH / 2 - CANVAS_BOUNDARY_BUTTON_SIZE / 2,
                top: cardBottom + CANVAS_BOUNDARY_LINE_LENGTH,
              }}
              aria-label={`Move down one level from ${display.title}`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => onSelect(parentId)}
            >
              <ArrowDown />
            </Button>
          )
        })}
        <AnimatePresence initial={false}>
          {rows.flatMap((row) =>
            row.map((node) => {
              const position = layout.positions.get(node.record.id)
              if (!position) {
                return null
              }

              const display = resolveOrgChartDisplay(node.record, mapping)
              const color = getOrgChartColor(node.record, colorByKey, legend)
              const isSelected = node.record.id === selectedId
              const canSelect = mapping.clickSelectsNode === true
              const cardStyle: CSSProperties = {
                minHeight: CANVAS_CARD_HEIGHT,
                ...(color ? { borderLeftColor: color, borderLeftWidth: 4 } : {}),
              }

              const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (!canSelect || (event.key !== 'Enter' && event.key !== ' ')) {
                  return
                }

                event.preventDefault()
                onSelect(node.record.id)
              }

              return (
                <motion.div
                  key={node.record.id}
                  initial={{ opacity: 0, left: position.x, top: position.y }}
                  animate={{ opacity: 1, left: position.x, top: position.y }}
                  exit={{ opacity: 0 }}
                  transition={{
                    left: CANVAS_CAMERA_SPRING,
                    top: CANVAS_CAMERA_SPRING,
                    opacity: { duration: 0.18 },
                  }}
                  ref={(element) => registerCardElement(node.record.id, element)}
                  className="absolute"
                  style={{ width: CANVAS_NODE_WIDTH }}
                >
                    <Card
                      role={canSelect ? 'button' : undefined}
                      tabIndex={canSelect ? 0 : -1}
                      aria-label={canSelect ? `Select ${display.title}` : undefined}
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={canSelect ? () => onSelect(node.record.id) : undefined}
                      onKeyDown={handleKeyDown}
                      onPointerDown={canSelect ? (event) => event.stopPropagation() : undefined}
                      className={cn(
                        'relative select-none shadow-sm',
                        canSelect && 'cursor-pointer hover:border-primary/60',
                        isSelected && 'ring-2 ring-primary/40'
                      )}
                      style={cardStyle}
                    >
                      <OrgChartCardActions actions={visibleCardActions} record={node.record} title={display.title} />
                      <CardContent className="flex h-full flex-col gap-3 p-3">
                        <div className={cn('flex min-w-0 items-start gap-2', visibleCardActions.length > 0 && 'pr-8')}>
                          <OrgChartAvatar display={display} size="default" />
                          <div className="min-w-0">
                            <p className="line-clamp-2 text-sm font-semibold">{display.title}</p>
                            {display.subtitle && display.subtitle !== display.title && (
                              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{display.subtitle}</p>
                            )}
                          </div>
                        </div>
                        {display.badges.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {display.badges.map((badge) => (
                              <Badge key={badge} variant="secondary">
                                {badge}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                </motion.div>
              )
            })
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function OrgChartLegend({ entries, attributeLabel }: { entries: OrgChartLegendEntry[]; attributeLabel?: string }) {
  if (entries.length === 0) {
    return null
  }

  return (
    <aside className="w-44 shrink-0 overflow-y-auto border-l border-border p-3" aria-label="Organisation chart legend">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {attributeLabel ?? 'Legend'}
      </p>
      <div className="flex flex-col gap-2">
        {entries.map((entry) => (
          <div key={entry.value} className="flex min-w-0 items-center gap-2 text-sm">
            <span
              className="size-3 shrink-0 rounded-full"
              style={{ backgroundColor: entry.color }}
              aria-hidden="true"
            />
            <span className="truncate" title={entry.label}>
              {entry.label}
            </span>
          </div>
        ))}
      </div>
    </aside>
  )
}

function OrgChartEmptyState() {
  return (
    <div className="flex h-full min-h-[240px] items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <h3 className="text-base font-semibold">No organisation records</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          This organisation chart does not have any positions to display.
        </p>
      </div>
    </div>
  )
}

export function OrgChartContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: OrgChartUISchema
  dataSchema: OrgChartDataSchema
  tileFilters?: ReactNode
}) {
  const { title, description, emphasizeHeader } = uiSchema
  const hasHeader = Boolean(title || description || tileFilters)
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const records = dataSchema.data ?? EMPTY_ORG_CHART_RECORDS
  const hierarchy = useMemo(() => buildOrgChartHierarchy(records), [records])
  const [selectedId, setSelectedId] = useState<string | null>(() => hierarchy.roots[0]?.record.id ?? null)
  const [search, setSearch] = useState('')
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(hierarchy.roots.filter((root) => root.children.length > 0).map((root) => root.record.id))
  )
  const [colorByKey, setColorByKey] = useState<string | null>(null)

  useEffect(() => {
    setSelectedId((current) =>
      current && hierarchy.nodesById.has(current) ? current : (hierarchy.roots[0]?.record.id ?? null)
    )
    setExpandedIds(new Set(hierarchy.roots.filter((root) => root.children.length > 0).map((root) => root.record.id)))
  }, [hierarchy])

  const visibleNodeIds = useMemo(
    () => getVisibleOrgChartNodeIds(hierarchy, search, uiSchema.sidebar),
    [hierarchy, search, uiSchema.sidebar]
  )
  const canvasRows = useMemo(() => getOrgChartCanvasRows(hierarchy, selectedId), [hierarchy, selectedId])
  const legend = useMemo(() => deriveOrgChartLegend(records, colorByKey), [records, colorByKey])
  const selectedColorAttribute = uiSchema.colorByAttributes.find((attribute) => attribute.key === colorByKey)

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const expandAncestors = useCallback(
    (id: string) => {
      setExpandedIds((current) => {
        const next = new Set(current)
        const visited = new Set<string>()
        let parentId = hierarchy.parentById.get(id) ?? null
        let changed = false

        while (parentId && !visited.has(parentId)) {
          visited.add(parentId)
          if (!next.has(parentId)) {
            next.add(parentId)
            changed = true
          }
          parentId = hierarchy.parentById.get(parentId) ?? null
        }

        return changed ? next : current
      })
    },
    [hierarchy]
  )

  const handleCanvasSelect = useCallback(
    (id: string) => {
      setSelectedId(id)
      expandAncestors(id)
    },
    [expandAncestors]
  )

  const handleColorByChange = useCallback((value: string) => {
    setColorByKey(value === 'none' ? null : value)
  }, [])

  return (
    <div className="flex h-full min-h-0 flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          {tileFilters}
        </ComponentHeader>
      )}
      <div className="min-h-0 flex-1" style={{ height: contentHeight }}>
        {records.length === 0 ? (
          <OrgChartEmptyState />
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-3 py-2">
              <div className="flex items-center gap-2">
                <label htmlFor="org-chart-color-by" className="text-sm text-muted-foreground">
                  Colour by
                </label>
                <Select value={colorByKey ?? 'none'} onValueChange={handleColorByChange}>
                  <SelectTrigger id="org-chart-color-by" className="h-8 w-48" aria-label="Colour by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {uiSchema.colorByAttributes.map((attribute) => (
                      <SelectItem key={attribute.key} value={attribute.key}>
                        {attribute.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="hidden text-xs text-muted-foreground sm:inline">Drag to pan · Scroll to zoom</span>
            </div>
            <div className="flex min-h-0 flex-1">
              <OrgChartSidebar
                hierarchy={hierarchy}
                sidebarMapping={uiSchema.sidebar}
                visibleNodeIds={visibleNodeIds}
                search={search}
                selectedId={selectedId}
                expandedIds={expandedIds}
                isCollapsed={isSidebarCollapsed}
                onSearchChange={setSearch}
                onSelect={setSelectedId}
                onToggleExpanded={toggleExpanded}
                onToggleCollapsed={() => setIsSidebarCollapsed((current) => !current)}
              />
              <div className="flex min-h-0 min-w-0 flex-1">
                {canvasRows.length > 0 ? (
                  <OrgChartCanvas
                    hierarchy={hierarchy}
                    rows={canvasRows}
                    selectedId={selectedId}
                    mapping={uiSchema.canvas}
                    colorByKey={colorByKey}
                    legend={legend}
                    cardActions={uiSchema.cardActions}
                    onSelect={handleCanvasSelect}
                  />
                ) : (
                  <div className="flex min-w-0 flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
                    Select a person to inspect their reporting subtree.
                  </div>
                )}
                <OrgChartLegend entries={legend} attributeLabel={selectedColorAttribute?.label} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function OrgChartContentLoading() {
  return (
    <div className="flex h-full min-h-[240px] min-w-0 animate-pulse">
      <div className="w-80 shrink-0 border-r border-border p-3">
        <Skeleton className="h-9 w-full" />
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-center">
        <Skeleton className="h-36 w-56" />
      </div>
    </div>
  )
}
